import React, {
  useContext, useEffect, useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

/* Localisation strings */
import { MDText } from 'i18n-react';

import { CarSideWindowsIcon, ProfileIcon, SignOutIcon } from '@motorway/motorway-storybook-cra';

import cypressIds from 'CypressId';

import { removeUser } from 'Context/apiActions';
import { UserContext } from 'Context/user';

import { GA_TAGS } from 'Utilities/analytics';
import { applyCypressData } from 'Utilities/index';
import useMediaQuery from 'Utilities/useMediaQuery';

import Texts from '../../../texts.json';

import styles from './Header.scss';

const T = new MDText(Texts);

const CSS_IDS = Object.freeze({
  ACCOUNT_TOGGLE: 'header-account-toggle',
  NAV_MOBILE: 'userNavMobile',
  SHIFT_RIGHT: 'mw-shift-right',
});

const AccountLinks = () => {
  let initials;

  const isMobile = useMediaQuery(`(max-width: ${styles.breakpointTablet}), (pointer: coarse)`);

  const [menuOpen, setMenuOpen] = useState(false);

  const menuElem = useRef(null);

  const { userState: user } = useContext(UserContext);
  const isUser = Number.isInteger(user?.id);

  if (isUser) {
    initials = user.name.charAt(0);
    if (user.name.split(' ').length > 1) {
      initials += user.name.split(' ')[1].charAt(0);
    }
    initials = initials.toUpperCase();
  }

  const listenForCloseEvent = (e) => {
    if (
      e.target.matches(`#${CSS_IDS.NAV_MOBILE}`) === false
      && e.target.matches(`#${CSS_IDS.ACCOUNT_TOGGLE}`) === false
    ) {
      setMenuOpen(false);

      document.body.removeEventListener('click', listenForCloseEvent);
      document.documentElement.classList.remove(CSS_IDS.SHIFT_RIGHT);
    }
  };

  const toggleMenu = (e) => {
    const mobileMenuClosed = (isMobile && !menuOpen);
    if (mobileMenuClosed || !isMobile) {
      GA_TAGS.HEADER_USERNAME_CLICKED();
    }

    if (e) {
      if (isMobile && e.target.getAttribute('target') !== '_blank') {
        e.preventDefault(); // Open menu on mobile
      } else {
        return; // Let desktop go to account
      }
    }

    setMenuOpen(!menuOpen);

    const vector = mobileMenuClosed ? 'add' : 'remove';

    document.body[`${vector}EventListener`]('click', listenForCloseEvent);
    document.documentElement.classList[vector](CSS_IDS.SHIFT_RIGHT);
  };

  useEffect(() => {
    if (!isMobile) {
      setMenuOpen(false);
      document.documentElement.classList.remove(CSS_IDS.SHIFT_RIGHT);
    }
  }, [isMobile]);

  useEffect(() => {
    if (menuOpen) {
      window.scrollTo(0, 0);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (menuElem.current && !menuOpen) {
      menuElem.current.scrollTop = 0;
    }
  }, [menuElem, menuOpen]);

  return (
    <div className={cx(styles.account, styles.toggle)}>
      {isUser ? (
        <>
          <Link
            className={cx(styles.user)}
            {...applyCypressData(cypressIds.links.accountLink)}
            id={CSS_IDS.ACCOUNT_TOGGLE}
            onClick={toggleMenu}
            to="/account"
          >
            <div className={styles.initials}>{initials}</div>
            <div className={styles.name}>{user.name.split(' ')[0]}</div>
          </Link>
          <div className={cx(styles.nav, styles.navDesktop, styles.subMenu)}>
            <ConstantAccountLinks />
          </div>

          <div ref={menuElem} className={cx(styles.nav, styles.navMobile)} id={CSS_IDS.NAV_MOBILE} {...applyCypressData(cypressIds.sections.mobileNav)}>
            <ConstantAccountLinks />
          </div>
        </>
      ) : (
        <Link aria-label="Sign in" className={cx(styles.signIn)} data-cy="signInLink" to="/sign-in">
          <ProfileIcon />
          <span className="signInLabel">
            {T.translate('components.navigation.signInLabel')}
          </span>
        </Link>
      )}
    </div>
  );
};

const ConstantAccountLinks = () => {
  const handleLogoutClick = async (e) => {
    e.preventDefault();
    removeUser();
  };

  const accountOnClick = () => {
    GA_TAGS.HEADER_ACCOUNT_CLICKED();
  };

  return (
    <ul>
      <li className={ styles.navItem }>
        <Link onClick={accountOnClick} to="/account">
          <CarSideWindowsIcon />
          {T.translate('accountView.title')}
        </Link>
      </li>
      <li className={ styles.navItem }>
        <a href="#signout" onClick={handleLogoutClick}>
          <SignOutIcon />
          {T.translate('components.navigation.signOutLabel')}
        </a>
      </li>
    </ul>
  );
};

export default AccountLinks;
